import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth";
import { Icon } from "../components/Icon";
import type { ExportFormat, ExportHistoryItem, ExportResult } from "../lib/api";
import { localizedError } from "../lib/localized-error";
import { useTasks } from "../tasks";

const formats = ["TXT", "SRT", "VTT", "DOCX", "PDF", "CSV", "JSON"] as const;
type DisplayFormat = (typeof formats)[number];

export function DeliveryPage() {
  const { t, i18n } = useTranslation();
  const { api, openAuth, session } = useAuth();
  const { tasks } = useTasks();
  const [format, setFormat] = useState<DisplayFormat>("SRT");
  const [options, setOptions] = useState({ speakers: true, timecodes: true });
  const [ready, setReady] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [history, setHistory] = useState<ExportHistoryItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    let active = true;
    void api.listExports().then((items) => { if (active) setHistory(items); }).catch(() => undefined);
    return () => { active = false; };
  }, [api, session]);

  async function refreshHistory() {
    const items = await api.listExports().catch(() => null);
    if (items) setHistory(items);
  }

  async function prepareExport(download: boolean): Promise<ExportResult | null> {
    const chosen = selected.size
      ? tasks.filter((item) => selected.has(item.id) && item.status === "completed")
      : tasks.filter((item) => item.status === "completed").slice(0, 1);
    if (!chosen.length) { setError(t("editor.noDocument")); return null; }
    setBusy(true);
    setReady(false);
    setError(null);
    try {
      const kind = format === "SRT" || format === "VTT" ? "subtitle" : "transcript";
      const items = [];
      for (const task of chosen) {
        const result = await api.getTranscriptionResult(task.id);
        const document = result.documents.find((item) => item.kind === kind) ?? result.documents[0];
        if (!document) { setError(t("editor.noDocument")); return null; }
        items.push({ document_id: document.id, revision_id: document.current_revision_id });
      }
      const outputFormat = format.toLowerCase() as ExportFormat;
      const applicableOptions = exportOptions(format, options);
      const created = items.length > 1
        ? await api.createBatchExport(items, outputFormat, applicableOptions)
        : await api.createExport(items[0]!.document_id, items[0]!.revision_id, outputFormat, applicableOptions);
      setReady(true);
      if (download) await api.downloadExport(created);
      await refreshHistory();
      return created;
    } catch (reason) {
      setError(localizedError(reason, t));
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function emailExport() {
    if (!session?.user) { openAuth(); return; }
    const created = await prepareExport(false);
    if (!created) return;
    setBusy(true);
    try {
      await api.emailExport(created.id);
      setReady(true);
      await refreshHistory();
    } catch (reason) {
      setError(localizedError(reason, t));
    } finally {
      setBusy(false);
    }
  }

  async function downloadExisting(item: ExportHistoryItem) {
    if (!item.download_url) return;
    setBusy(true);
    setError(null);
    try {
      await api.downloadExport(item as ExportResult);
    } catch (reason) {
      setError(localizedError(reason, t));
    } finally {
      setBusy(false);
    }
  }

  const optionKeys = availableOptions(format);

  return <div className="page delivery-page">
    <header className="page-title"><div><p className="eyebrow">{t("delivery.eyebrow")}</p><h1>{t("delivery.title")}</h1></div></header>
    <div className="delivery-layout">
      <main className="panel delivery-main">
        <section><h2>{t("delivery.format")}</h2><div className="format-grid">{formats.map((item) => <button key={item} className={format === item ? "is-active" : ""} onClick={() => { setFormat(item); setReady(false); }}><strong>{item}</strong><span>{t(`delivery.kind.${formatKind(item)}`)}</span>{format === item && <Icon name="check" />}</button>)}</div></section>
        <section><h2>{t("delivery.options")}</h2>{optionKeys.length ? <div className="option-list">{optionKeys.map((key) => <label key={key}><input type="checkbox" checked={options[key]} onChange={(event) => setOptions({ ...options, [key]: event.target.checked })} /><span>{t(`delivery.${key}`)}</span></label>)}</div> : <p className="delivery-option-note">{t("delivery.fixedStructure")}</p>}</section>
        <button className="button button--primary export-cta" disabled={busy} onClick={() => void prepareExport(true)}><Icon name="download" />{busy ? t("common.loading") : t("delivery.download")}</button>
        {error && <p className="form-error" role="alert">{error}</p>}
        {ready && <div className="success-note"><Icon name="check" /><p><strong>{t("delivery.ready")}</strong><small>{t("delivery.readyHint")}</small></p></div>}
      </main>
      <aside className="panel delivery-side"><h2>{t("delivery.files")}</h2>{tasks.slice(0, 8).map((task) => <label key={task.id}><input type="checkbox" checked={selected.has(task.id)} disabled={task.status !== "completed"} onChange={(event) => { const next = new Set(selected); if (event.target.checked) next.add(task.id); else next.delete(task.id); setSelected(next); setReady(false); }} /><span><strong>{task.title}</strong><small>{t(`status.${task.status}`, { defaultValue: task.status })}</small></span></label>)}<button className="button button--ghost" disabled={busy} onClick={() => void emailExport()}><Icon name="mail" />{t("delivery.email")}</button></aside>
    </div>
    <section className="delivery-history"><header className="section-heading"><div><p className="eyebrow">{t("delivery.historyEyebrow")}</p><h2>{t("delivery.history")}</h2></div></header>{history.length ? <div className="panel delivery-history-list">{history.map((item) => <article key={item.id}><span className="delivery-format">{item.format.toUpperCase()}</span><p><strong>{item.title}</strong><small>{new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(item.created_at_ms)} · {item.revision_id.slice(0, 8)}</small></p><span className={`delivery-state delivery-state--${item.status}`}>{t(`delivery.status.${item.status}`, { defaultValue: item.status })}</span>{item.email_status && <span className={`delivery-state delivery-state--${item.email_status}`}><Icon name="mail" />{t(`delivery.emailStatus.${item.email_status}`)}</span>}<button type="button" disabled={busy || !item.download_url} onClick={() => void downloadExisting(item)}><Icon name="download" />{item.download_url ? t("delivery.downloadAgain") : t("delivery.expired")}</button></article>)}</div> : <div className="panel empty-state"><Icon name="download" /><strong>{t("delivery.noHistory")}</strong><span>{t("delivery.noHistoryHint")}</span></div>}</section>
  </div>;
}

function availableOptions(format: DisplayFormat): Array<"speakers" | "timecodes"> {
  if (format === "SRT" || format === "VTT") return ["speakers"];
  if (format === "TXT" || format === "DOCX" || format === "PDF") return ["speakers", "timecodes"];
  return [];
}

function exportOptions(format: DisplayFormat, options: { speakers: boolean; timecodes: boolean }): Record<string, boolean> {
  const keys = availableOptions(format);
  return Object.fromEntries(keys.map((key) => [key, options[key]]));
}

function formatKind(format: DisplayFormat): "subtitle" | "document" | "structured" {
  if (format === "SRT" || format === "VTT") return "subtitle";
  if (format === "TXT" || format === "DOCX" || format === "PDF") return "document";
  return "structured";
}
